import { useQuery } from '@tanstack/react-query';
import { api } from '../api-client.config';

/**
 * Cross-fixture leader candidate (DKPLAT-354).
 *
 * Returned by GET /api/admin/pickems/:pickemId/leader-candidates and consumed
 * by the leader question builder. statValue defaults to 0 until the
 * backend wires up live / season-stat aggregation.
 */
export interface LeaderCandidate {
  externalId: string;
  name: string;
  kind: 'player' | 'team';
  position?: string;
  teamExternalId?: string;
  teamShortcode?: string;
  teamName?: string;
  imageUrl?: string;
  fixtureLabel?: string;
  matchTime?: string;
  statValue: number;
}

export interface LeaderCandidatesResponse {
  candidates: LeaderCandidate[];
  fixtureIds: string[];
  total: number;
  truncated: boolean;
  statType: string | null;
  kind: 'player' | 'team';
  /**
   * Human label for the season stat populated on each candidate's
   * `statValue` (e.g. "Batting Avg" for MLB, "PPG" for NBA). V1 limitation:
   * this is the feed's primary sport stat, not necessarily the same as the
   * admin's chosen grading stat. V2 will align these.
   */
  statValueLabel?: string;
}

export interface LeaderCandidatesParams {
  pickemId: string;
  kind: 'player' | 'team';
  statType?: string;
  /** Default 30 from the backend; set `all` to disable the cap. */
  limit?: number;
  all?: boolean;
  /**
   * Admin's explicit fixture selection. When supplied, the backend draws
   * candidates from exactly these fixtures. When omitted, the backend falls
   * back to the auto-window (every fixture for the sport between `from`
   * and `to`) — see leader-candidates.controller.ts.
   */
  fixtureIds?: string[];
}

export const getLeaderCandidates = async (
  params: LeaderCandidatesParams
): Promise<LeaderCandidatesResponse> => {
  const search = new URLSearchParams();
  search.set('kind', params.kind);
  if (params.statType) search.set('statType', params.statType);
  if (params.limit) search.set('limit', String(params.limit));
  if (params.all) search.set('all', 'true');
  if (params.fixtureIds && params.fixtureIds.length > 0) {
    search.set('fixtureIds', params.fixtureIds.join(','));
  }

  // The shared `api` client already mounts under /api/admin — sending the
  // full path here caused the duplicated /api/admin/api/admin/... 404s.
  const response = (await api.get(
    `/pickems/${params.pickemId}/leader-candidates?${search.toString()}` as any
  )) as LeaderCandidatesResponse;
  return response;
};

export const useGetLeaderCandidatesQuery = (params: LeaderCandidatesParams, enabled = true) => {
  return useQuery({
    queryKey: [
      'admin',
      'pickems',
      params.pickemId,
      'leader-candidates',
      params.kind,
      params.statType ?? null,
      params.all ?? false,
      (params.fixtureIds ?? []).join(','),
    ],
    queryFn: () => getLeaderCandidates(params),
    enabled: enabled && !!params.pickemId,
    staleTime: 60 * 1000, // 1 minute — admin will refresh-click while configuring
  });
};
